/**
 * The call screen. One button, and the honest state of everything behind it.
 *
 * THE WHOLE SCREEN IS ONE DECISION: a person should be able to start a real
 * conversation without reading anything, and a person debugging that
 * conversation should be able to see every number we have without leaving. So
 * there is exactly one primary control — start, or end — and everything
 * diagnostic is collapsed by default and one click away.
 *
 * WHY THE CALL LIVES IN A `CallSession` OBJECT AND NOT IN THIS COMPONENT. A call
 * holds a peer connection, two WebSockets, two workers, an AudioContext and a
 * microphone, and it must survive every re-render this page does — a transcript
 * delta arrives every few hundred milliseconds. React state cannot own that
 * safely: the first version of this screen drove it with `useEffect`, and Strict
 * Mode's double mount started two calls, because a WebRTC offer is not
 * idempotent. So the session is created once and read through
 * `useSyncExternalStore`, and this file is a pure function of a snapshot.
 *
 * WHY THE SUPPORT PROBE RUNS ON MOUNT AND NOT ON CLICK. On a blocked platform —
 * iOS, where WebRTC and Web Audio are suspended on screen lock and the
 * microphone goes muted with no workaround — the button must be disabled with
 * the reason visible BEFORE anybody presses it. Finding out after the
 * microphone prompt is a worse demo than finding out at all.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO: interpret. It shows the state the
 * Durable Object reports, including the three `LIVE_DEGRADED_*` states, without
 * softening them into "connecting". A degraded call is a working conversation
 * with one of OUR components down, and that distinction is the entire reason the
 * media path bypasses us.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  CheckIcon,
  ChevronsUpDownIcon,
  CircleAlertIcon,
  MicIcon,
  MicOffIcon,
  PhoneCallIcon,
  PhoneOffIcon,
} from 'lucide-react'
import { toolDefs, type AgentConfig } from '@/api'
import type { ToolMeta } from '@/components/call/transcript'
import { CallSession, probe, type SupportReport } from '@/lib/call'
import { Link, navigate } from '@/components/app-sidebar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PageHeader } from '@/components/data-table'
import { StatePill } from '@/components/state-pill'
import { Elapsed, LiveDot } from '@/components/call/call-status'
import { LiveTranscript } from '@/components/call/transcript'
import { DiagnosticsPanel, LiveStats } from '@/components/call/diagnostics'
import { cn } from '@/lib/utils'

/**
 * Reserved query keys — routing, not facts about the caller. Only `agent`
 * today: it selects which agent the page calls (see App.tsx) and forwarding it
 * as a param would tell the customer's webhook something it already knows in a
 * field it might mistake for identity.
 */
const RESERVED_QUERY_KEYS = new Set(['agent'])

/**
 * Everything else in the query string, as session params. Undefined when there
 * is nothing to send, so the request body stays byte-identical to what it was
 * before params existed. A repeated key keeps its LAST value — the rightmost
 * occurrence is the one somebody just appended to the URL by hand.
 */
function paramsFromSearch(): Record<string, string> | undefined {
  const out: Record<string, string> = {}
  let any = false
  for (const [k, v] of new URLSearchParams(location.search)) {
    if (RESERVED_QUERY_KEYS.has(k)) continue
    out[k] = v
    any = true
  }
  return any ? out : undefined
}

export function CallPage({
  agentId,
  agents,
}: {
  agentId: string
  /** The app-wide agent list, for the picker. Null while it loads. */
  agents: AgentConfig[] | null
}) {
  // Created once. Never recreated, never keyed on props: an in-flight call must
  // not be discarded because the agent list refreshed upstream.
  const [call] = useState(() => new CallSession())
  const snap = useSyncExternalStore(call.subscribe, call.getSnapshot)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [support, setSupport] = useState<SupportReport | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Tool descriptions for the transcript's tool cards. Looked up from the
  // panel's own library, NOT received over the call socket — the socket carries
  // names only (embed visitors share it). Absent map = cards show the mono name.
  const [toolMeta, setToolMeta] = useState<Record<string, ToolMeta>>({})

  useEffect(() => {
    const ac = new AbortController()
    toolDefs
      .list(ac.signal)
      .then((res) => {
        const map: Record<string, ToolMeta> = {}
        for (const t of res.rows) {
          map[t.name] = {
            description: t.description,
            displayName: t.displayName,
            displayDescription: t.displayDescription,
          }
        }
        setToolMeta(map)
      })
      .catch(() => {
        // Cosmetic context only — a transcript with plain tool names is not
        // worth an error banner.
      })
    return () => ac.abort()
  }, [])

  /**
   * Choosing an agent rewrites the URL rather than setting local state: the
   * `agent` query param is already this page's source of truth (App.tsx reads
   * it and passes `agentId` back down), and it is what makes a picked agent
   * shareable and refresh-proof. Every OTHER query key is preserved verbatim —
   * they are the client params riding into the next session.
   */
  function pickAgent(id: string) {
    setPickerOpen(false)
    if (id === agentId) return
    const params = new URLSearchParams(location.search)
    params.set('agent', id)
    navigate(`/call?${params.toString()}`)
  }

  const selectedAgent = agents?.find((a) => a.id === agentId) ?? null

  useEffect(() => {
    let alive = true
    probe()
      .then((r) => {
        if (alive) setSupport(r)
      })
      .catch(() => {
        // A probe that throws is itself a support answer; the call attempt will
        // produce the real error, so this only costs the pre-flight warning.
        if (alive) setSupport(null)
      })
    return () => {
      alive = false
    }
  }, [])

  // Leaving the screen ends the call. Reading the phase from the session rather
  // than from the closed-over snapshot matters: Strict Mode runs this cleanup on
  // the throwaway first mount, when there is nothing to end.
  useEffect(() => {
    return () => {
      const phase = call.getSnapshot().phase
      if (phase === 'live' || phase === 'starting' || phase === 'ending') void call.end()
    }
  }, [call])

  const blocked = support?.blocked ?? null
  const busy = snap.phase === 'checking' || snap.phase === 'starting' || snap.phase === 'ending'
  const live = snap.phase === 'live'

  function onPrimary() {
    if (live) {
      void call.end()
      return
    }
    const el = audioRef.current
    if (!el) return
    // Every non-reserved query key rides into the session as a client param,
    // for the agent's init webhook and HTTP tools: /call?agent=…&userID=Andrew
    // is the demo path for a webhook that rewrites the prompt per caller. Read
    // AT CLICK TIME from the CURRENT URL, not from props captured at mount —
    // this app's router rewrites the query without remounting the page, and a
    // param edited in the address bar between two calls belongs to the second.
    void call.start({ agentId, audioEl: el, params: paramsFromSearch() })
  }

  return (
    <div className="min-w-0">
      {/*
        The remote track is attached to this element and it must stay in the
        document. `sr-only` keeps it rendered — `hidden` would remove it from the
        layout, and a media element the browser considers non-rendered is exactly
        the case where the Web Audio fallback recorder reads silence. Autoplay
        policy is satisfied because the element already exists when the user
        gesture that starts the call happens.
      */}
      <audio ref={audioRef} autoPlay playsInline className="sr-only" aria-hidden />

      <PageHeader
        title="Live call"
        subtitle="Talk to one of your agents right from this browser — pick an agent and start the conversation."
        actions={
          live ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => call.setMuted(!snap.muted)}
              aria-pressed={snap.muted}
            >
              {snap.muted ? <MicOffIcon className="size-3.5" /> : <MicIcon className="size-3.5" />}
              {snap.muted ? 'Unmute' : 'Mute'}
            </Button>
          ) : null
        }
      />

      {blocked ? (
        <div className="err mb-5 flex items-start gap-2">
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{blocked}</span>
        </div>
      ) : null}

      {snap.error ? (
        <div className="err mb-5 flex items-start gap-2">
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{snap.error}</span>
        </div>
      ) : null}

      {/* ---------------------------------------------------------------- stage */}
      <section className="panel">
        <h2>
          the call
          <span className="note">
            {snap.sessionId ? <span className="mono">{snap.sessionId}</span> : 'no session yet'}
          </span>
        </h2>
        <div className="body flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="field">
              <span className="label">agent</span>
              {/* Type-search picker. Disabled once a call is underway: the agent
                  is part of the session that already exists, so switching would
                  claim something the running call cannot honour. */}
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="h-12 w-64 justify-between text-base font-normal"
                    disabled={busy || live || agents === null}
                  >
                    <span className="truncate">
                      {selectedAgent?.name ?? (
                        <span className="mono">{agents === null ? 'loading agents…' : agentId}</span>
                      )}
                    </span>
                    <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search agents…" />
                    <CommandList>
                      <CommandEmpty>No agent matches.</CommandEmpty>
                      {(agents ?? []).map((a) => (
                        <CommandItem key={a.id} value={a.name} onSelect={() => pickAgent(a.id)}>
                          <CheckIcon
                            className={cn('size-4', a.id === agentId ? 'opacity-100' : 'opacity-0')}
                          />
                          <span className="truncate">{a.name}</span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <Button
              size="lg"
              variant={live ? 'destructive' : 'default'}
              className="h-12 min-w-56 text-base"
              disabled={blocked !== null || busy}
              onClick={onPrimary}
            >
              {live ? (
                <>
                  <PhoneOffIcon className="size-4" /> End the call
                </>
              ) : busy ? (
                <>
                  <span className="live-dot" aria-hidden /> {snap.phase === 'ending' ? 'Finishing…' : 'Connecting…'}
                </>
              ) : (
                <>
                  <PhoneCallIcon className="size-4" />
                  {snap.phase === 'ended' || snap.phase === 'failed' ? 'Start another call' : 'Start conversation'}
                </>
              )}
            </Button>

            {live ? <LiveDot /> : null}

            <div className="field">
              <span className="label">elapsed</span>
              <Elapsed fromMs={snap.startedAtMs} toMs={live ? null : snap.endedAtMs} />
            </div>

            <div className="field">
              <span className="label">session state</span>
              {snap.serverState ? (
                <span>
                  <StatePill state={snap.serverState} />
                </span>
              ) : (
                <span className="pill pill-plain">not reported yet</span>
              )}
            </div>

            {snap.serverStateDetail ? (
              <div className="field">
                <span className="label">detail</span>
                <span className="text-sm text-muted-foreground">{snap.serverStateDetail}</span>
              </div>
            ) : null}
          </div>

          {snap.notices.length > 0 ? (
            <ul className="flex flex-col gap-1.5 rounded-md border border-ev-warn-border bg-ev-warn-bg px-3 py-2 text-sm text-ev-warn">
              {snap.notices.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
          ) : null}

          {snap.phase === 'ended' || snap.phase === 'failed' ? (
            <div className="grid2">
              <div className="field">
                <span className="label">session</span>
                <span className="mono text-sm">{snap.sessionId ?? '—'}</span>
              </div>
              <div className="field">
                <span className="label">what happens now</span>
                <span className="text-sm text-muted-foreground">
                  {snap.sessionId ? (
                    <>
                      The recording finalises in the background, and the turn-by-turn latency decomposition appears
                      on{' '}
                      <Link to={`/sessions/${encodeURIComponent(snap.sessionId)}`} className="underline underline-offset-4">
                        the session page
                      </Link>
                      . Evidence state starts at <span className="mono">PENDING</span>; it is not a defect.
                    </>
                  ) : (
                    'No session was created, so there is nothing to look at.'
                  )}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* ------------------------------------------- transcript + live numbers
          Half-and-half above lg, stacked below it. The two bodies share one
          fixed height so the row reads as a pair; the transcript scrolls
          inside its half, and everything deeper stays in the collapsed
          diagnostics panel underneath. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="panel mb-0">
          <h2>
            transcript
            <span className="note">
              from the Durable Object’s sideband, not from this browser
              {/* Only while the call is LIVE is a closed control socket news. `end()`
                  closes it deliberately, so after that this claimed the channel was
                  "down, resuming" on a call that had finished normally — the same
                  mistake as the warning banner, in a second place. */}
              {!snap.controlUp && snap.phase === 'live' ? ' · control channel down, resuming' : ''}
            </span>
          </h2>
          <div className="body flush">
            <LiveTranscript
              lines={snap.transcript}
              tools={snap.tools}
              cards={snap.cards}
              toolMeta={toolMeta}
              speaking={snap.speaking}
              className={cn('h-[32rem]')}
            />
          </div>
        </section>

        <section className="panel mb-0">
          <h2>
            live numbers
            <span className="note">connection and network, this browser’s clock</span>
          </h2>
          <div className="body h-[32rem] overflow-y-auto">
            <LiveStats snap={snap} />
          </div>
        </section>
      </div>

      <div className="mt-4">
        <DiagnosticsPanel snap={snap} />
      </div>

      {support && support.warnings.length > 0 && snap.phase === 'idle' ? (
        <ul className="mt-5 flex flex-col gap-1.5 text-xs text-muted-foreground">
          {support.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
